import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import demoStrategies, { getDemoStrategy, getMapMeta } from '../data/demoStrategies';
import '../styles/StrategyRoom.css';
import '../styles/Demo.css';

const cloneSteps = (steps = []) =>
  steps.map((s) => ({
    ...s,
    players: s.players ? s.players.map((p) => ({ ...p })) : [],
    utilities: s.utilities ? s.utilities.map((u) => ({ ...u })) : [],
    paths: s.paths
      ? s.paths.map((path) => ({
          ...path,
          points: path.points ? path.points.map((pt) => ({ ...pt })) : [],
        }))
      : [],
  }));

const utilityIcons = {
  smoke: '/assets/smoke.png',
  grenade: '/assets/grenade-icon.png',
  trophy: '/assets/trophy-icon.png',
  flash: '/assets/flash.png',
};

const DemoStrategy = () => {
  const { demoId } = useParams();
  const navigate = useNavigate();

  const strategy = useMemo(
    () => getDemoStrategy(demoId) || demoStrategies[0],
    [demoId]
  );

  const [steps, setSteps] = useState(() => cloneSteps(strategy?.steps));
  const [initialSteps, setInitialSteps] = useState(() =>
    cloneSteps(strategy?.steps)
  );
  const [activeStepIndex, setActiveStepIndex] = useState(0);
  const [drawMode, setDrawMode] = useState(false);
  const [isPainting, setIsPainting] = useState(false);
  const [dragState, setDragState] = useState(null);
  const boardRef = useRef(null);

  useEffect(() => {
    setSteps(cloneSteps(strategy?.steps));
    setInitialSteps(cloneSteps(strategy?.steps));
    setActiveStepIndex(0);
    setDrawMode(false);
    setIsPainting(false);
    setDragState(null);
  }, [strategy]);

  const currentStep = steps?.[activeStepIndex] || {};
  const mapMeta = getMapMeta(strategy?.mode, strategy?.map);
  const mapImage = mapMeta?.image || '/assets/summ.jpg';

  const percentFromEvent = (e) => {
    const rect = boardRef.current?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    const x = Math.min(100, Math.max(0, ((e.clientX - rect.left) / rect.width) * 100));
    const y = Math.min(100, Math.max(0, ((e.clientY - rect.top) / rect.height) * 100));
    return { x, y };
  };

  const startPath = (e) => {
    if (!drawMode) return;
    const { x, y } = percentFromEvent(e);
    setSteps((prev) => {
      const next = [...prev];
      const step = { ...next[activeStepIndex] };
      const newPath = {
        color: '#7e57c2',
        width: 3,
        opacity: 0.9,
        points: [{ x, y }],
      };
      step.paths = [...(step.paths || []), newPath];
      next[activeStepIndex] = step;
      return next;
    });
    setIsPainting(true);
  };

  const extendPath = (e) => {
    if (!isPainting) return;
    const { x, y } = percentFromEvent(e);
    setSteps((prev) => {
      const next = [...prev];
      const step = { ...next[activeStepIndex] };
      const paths = [...(step.paths || [])];
      if (!paths.length) return prev;
      const lastPath = { ...paths[paths.length - 1] };
      lastPath.points = [...(lastPath.points || []), { x, y }];
      paths[paths.length - 1] = lastPath;
      step.paths = paths;
      next[activeStepIndex] = step;
      return next;
    });
  };

  const startDrag = (type, id, e) => {
    e.stopPropagation();
    setDragState({ type, id });
  };

  useEffect(() => {
    const handleMove = (e) => {
      if (isPainting) {
        extendPath(e);
      }
      if (!dragState) return;
      const { x, y } = percentFromEvent(e);

      setSteps((prev) => {
        const next = [...prev];
        const step = { ...next[activeStepIndex] };
        if (dragState.type === 'player') {
          step.players = step.players.map((p) =>
            p.id === dragState.id ? { ...p, x, y } : p
          );
        }
        if (dragState.type === 'utility') {
          step.utilities = step.utilities.map((u) =>
            u.id === dragState.id ? { ...u, x, y } : u
          );
        }
        next[activeStepIndex] = step;
        return next;
      });
    };

    const handleUp = () => {
      setDragState(null);
      setIsPainting(false);
    };

    window.addEventListener('mousemove', handleMove);
    window.addEventListener('mouseup', handleUp);
    return () => {
      window.removeEventListener('mousemove', handleMove);
      window.removeEventListener('mouseup', handleUp);
    };
  }, [dragState, isPainting, activeStepIndex]);

  const addUtility = (type) => {
    const center = { x: 50, y: 50 };
    const uid = `demo-${type}-${Date.now()}`;
    setSteps((prev) => {
      const next = [...prev];
      const step = { ...next[activeStepIndex] };
      step.utilities = [
        ...(step.utilities || []),
        { id: uid, type, x: center.x, y: center.y },
      ];
      next[activeStepIndex] = step;
      return next;
    });
  };

  const resetStep = () => {
    setSteps((prev) => {
      const next = [...prev];
      next[activeStepIndex] = cloneSteps([initialSteps[activeStepIndex]])[0];
      return next;
    });
  };

  const resetAll = () => {
    setSteps(cloneSteps(initialSteps));
    setDrawMode(false);
    setIsPainting(false);
    setDragState(null);
  };

  const mapLabel = `${strategy?.map?.replaceAll('_', ' ')} - ${strategy?.mode?.toUpperCase()}`;

  return (
    <div className="container" onContextMenu={(e) => e.preventDefault()}>
      <div className="top-bar" style={{ position: 'relative', zIndex: 2 }}>
        <div className="nav-left">
          <img src="/assets/theboxlogo.png" alt="TheBox Logo" className="logo-img" onClick={() => navigate('/')} />
          <nav className="nav-links">
            <a onClick={() => navigate('/demo')}>Demo Playbook</a>
            <a onClick={() => navigate('/landing')}>Product</a>
            <a onClick={() => navigate('/register')}>Start free</a>
          </nav>
        </div>
        <div className="nav-actions" style={{ gap: '8px' }}>
          <button className="secondary-btn" onClick={() => navigate('/demo')}>Back to demos</button>
          <button className="primary-btn" onClick={() => navigate('/register')}>Save this for real</button>
        </div>
      </div>

      <div className="main-content">
        <aside className="sidebar">
          <p className="eyebrow">Step {activeStepIndex + 1} of {steps.length}</p>
          <h2 style={{ margin: '6px 0' }}>{currentStep.title}</h2>
          <p className="subtext">{currentStep.description}</p>

          <div className="mode-switcher" style={{ marginTop: '12px', flexWrap: 'wrap', gap: '6px' }}>
            {steps.map((_, idx) => (
              <button
                key={`step-${idx}`}
                className={`mode-button ${idx === activeStepIndex ? 'active' : ''}`}
                onClick={() => {
                  setActiveStepIndex(idx);
                  setDrawMode(false);
                  setIsPainting(false);
                  setDragState(null);
                }}
              >
                Step {idx + 1}
              </button>
            ))}
          </div>

          <div style={{ marginTop: '12px', borderTop: '1px solid #333', paddingTop: '10px', display: 'grid', gap: '10px' }}>
            <div>
              <span className="eyebrow">Draw paths</span>
              <button
                className={drawMode ? 'secondary-btn' : 'ghost-btn'}
                style={{ width: '100%', marginTop: '6px' }}
                onClick={() => {
                  setDrawMode((v) => !v);
                  setIsPainting(false);
                }}
              >
                {drawMode ? 'Drawing... click/drag' : 'Toggle draw mode'}
              </button>
            </div>

            <div>
              <span className="eyebrow">Add utility</span>
              <div className="utility-row" style={{ marginTop: '6px' }}>
                {Object.keys(utilityIcons).map((util) => (
                  <button key={util} className="ghost-btn" onClick={() => addUtility(util)}>
                    {util}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <span className="eyebrow">Reset</span>
              <div className="utility-row" style={{ marginTop: '6px' }}>
                <button className="ghost-btn" onClick={resetStep}>This step</button>
                <button className="ghost-btn" onClick={resetAll}>All steps</button>
              </div>
            </div>
          </div>

          <div style={{ marginTop: '10px', padding: '10px', background: '#1e1e24', borderRadius: '10px', border: '1px solid rgba(255,255,255,0.06)' }}>
            <p className="subtext" style={{ margin: 0 }}>This is a sandbox. Edits stay local. Want to save + share? <button className="link-btn" onClick={() => navigate('/register')}>Create your team</button>.</p>
          </div>
        </aside>

        <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          <div
            className="map-canvas"
            ref={boardRef}
            onMouseDown={(e) => {
              if (drawMode) startPath(e);
            }}
            style={{ cursor: drawMode ? 'crosshair' : 'default', borderRadius: '12px' }}
          >
            <img
              src={mapImage}
              alt={mapLabel}
              style={{
                position: 'absolute',
                inset: 0,
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                filter: 'saturate(1.05)',
              }}
            />

            <svg className="map-overlay" viewBox="0 0 100 100" preserveAspectRatio="none">
              {currentStep.paths?.map((path, idx) => (
                <polyline
                  key={`path-${idx}`}
                  fill="none"
                  stroke={path.color || '#7e57c2'}
                  strokeWidth={(path.width || 3) / 5}
                  strokeOpacity={path.opacity || 0.9}
                  points={(path.points || []).map((p) => `${p.x},${p.y}`).join(' ')}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              ))}
            </svg>

            {currentStep.utilities?.map((util) => (
              <div
                key={util.id}
                className="demo-utility"
                style={{ left: `${util.x}%`, top: `${util.y}%` }}
                onMouseDown={(e) => startDrag('utility', util.id, e)}
              >
                <img src={utilityIcons[util.type] || utilityIcons.flash} alt={util.type} />
              </div>
            ))}

            {currentStep.players?.map((player) => (
              <div
                key={player.id}
                className="demo-player"
                style={{
                  left: `${player.x}%`,
                  top: `${player.y}%`,
                  backgroundColor: player.color || '#7e57c2',
                }}
                onMouseDown={(e) => startDrag('player', player.id, e)}
                title={player.role}
              >
                {player.label}
              </div>
            ))}

            <div className="demo-map-label">{mapLabel}</div>
          </div>

          <div className="bottom-panel" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ textAlign: 'left' }}>
              <p className="eyebrow" style={{ margin: 0 }}>Mock data only</p>
              <p className="subtext" style={{ margin: 0 }}>Drag players, drop utils, draw paths. Nothing is saved to your account.</p>
            </div>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button className="secondary-btn" onClick={() => navigate('/demo')}>Change demo</button>
              <button className="primary-btn" onClick={() => navigate('/register')}>Create account</button>
            </div>
          </div>
        </div>

        <aside className="right-panel" style={{ display: 'grid', gap: '12px' }}>
          <div>
            <p className="eyebrow">Highlights</p>
            <ul style={{ paddingLeft: '18px', margin: 0, color: '#c3c7d1', lineHeight: 1.5 }}>
              {strategy?.highlights?.map((h) => <li key={h}>{h}</li>)}
            </ul>
          </div>
          <div style={{ borderTop: '1px solid #333', paddingTop: '10px' }}>
            <p className="eyebrow">Ready to save?</p>
            <button className="primary-btn" style={{ width: '100%' }} onClick={() => navigate('/register')}>
              Save & share with team
            </button>
            <button className="ghost-btn" style={{ width: '100%', marginTop: '8px' }} onClick={() => navigate('/login')}>
              Sign in
            </button>
          </div>
        </aside>
      </div>
    </div>
  );
};

export default DemoStrategy;
